import React, {useCallback, useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import {useFormik} from 'formik';
import {useDispatch, useSelector} from 'react-redux';
import {debounce} from 'lodash';
import Dialog from '@material-ui/core/Dialog';
import DialogContainer from 'components/DialogContainer/styles';
import DialogTitle from 'components/DialogTitle/styles';
import DialogBody from 'components/DialogBody/styles';
import Subtitle14px from 'components/typography/Subtitle14px';
import {FlexContainer, FlexItem} from 'components/layout';
import AutocompleteField from 'components/AutocompleteField/AutocompleteField';
import Field from 'components/FieldInput';
import ButtonPrimary from 'components/buttons/ButtonPrimary/styles';
import {ButtonOutline} from 'components/buttons';
import DialogActions from 'components/DialogActions/styles';
import DialogAction from 'components/DialogAction/styles';
import {ErrorSign, ErrorSignWrapper} from 'components/errorSign/errorSign';
import {
  CLEAR_ACTIVE_LOCATIONS,
  CLEAR_LOCATION_OTTER_BRANDS,
  GET_LOCATION_OTTER_BRANDS,
  SEARCH_ACTIVE_LOCATIONS
} from 'store/byocVirtualNumbers/action';

const buildValidationSchema = (isMultiBrand) =>
  Yup.object().shape({
    locationId: Yup.string().trim().required('Location is required'),
    brandId: isMultiBrand
      ? Yup.string().trim().required('Brand Id is required')
      : Yup.string(),
    brandName: isMultiBrand
      ? Yup.string().trim().required('Brand Name is required')
      : Yup.string()
  });

const buildInitialValues = (number) => ({
  locationId: number?.locationId || '',
  locationName: number?.locationName || '',
  brandId: number?.brandId || '',
  brandName: number?.brandName || ''
});

const buildInitialLocation = (number) =>
  number?.locationId
    ? {id: number.locationId, name: number.locationName || number.locationId}
    : null;

const ByocVirtualNumberModal = ({number, onClose, onSubmit}) => {
  const dispatch = useDispatch();
  const isEdit = Boolean(number?.id);
  const {
    creating,
    updating,
    activeLocations,
    searchingLocations,
    loadingOtterBrands,
    otterBrands
  } = useSelector((state) => state.byocVirtualNumbersReducer);
  const [selectedLocation, setSelectedLocation] = useState(
    buildInitialLocation(number)
  );
  const [searchValue, setSearchValue] = useState('');

  const otterBrandsMatchLocation = Boolean(
    otterBrands?.locationId && otterBrands.locationId === selectedLocation?.id
  );
  const isMultiBrand = Boolean(
    otterBrandsMatchLocation && otterBrands?.isMultiBrand
  );
  const otterBrandsFailed = Boolean(
    otterBrandsMatchLocation && otterBrands?.failed
  );
  const saving = creating || updating;

  const validationSchema = useMemo(() => buildValidationSchema(isMultiBrand), [
    isMultiBrand
  ]);

  const formik = useFormik({
    initialValues: buildInitialValues(number),
    validationSchema,
    enableReinitialize: false,
    onSubmit: (values) => {
      onSubmit({
        locationId: values.locationId.trim(),
        brandId: isMultiBrand ? values.brandId.trim() : undefined,
        brandName: isMultiBrand ? values.brandName.trim() : undefined
      });
    }
  });

  const debouncedSearch = useCallback(
    debounce((value) => {
      dispatch(SEARCH_ACTIVE_LOCATIONS({search: value}));
    }, 500),
    [dispatch]
  );

  useEffect(() => {
    if (selectedLocation?.id) {
      dispatch(GET_LOCATION_OTTER_BRANDS({locationId: selectedLocation.id}));
    } else {
      dispatch(CLEAR_LOCATION_OTTER_BRANDS());
    }
  }, [selectedLocation?.id, dispatch]);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
      dispatch(CLEAR_ACTIVE_LOCATIONS());
      dispatch(CLEAR_LOCATION_OTTER_BRANDS());
    };
  }, [debouncedSearch, dispatch]);

  const handleClose = () => {
    dispatch(CLEAR_ACTIVE_LOCATIONS());
    dispatch(CLEAR_LOCATION_OTTER_BRANDS());
    onClose();
  };

  const onLocationSelected = (value) => {
    setSelectedLocation(value);
    setSearchValue('');
    formik.setFieldValue('locationId', value?.id || '');
    formik.setFieldValue('locationName', value?.name || '');
    const sameAsSavedLocation = Boolean(
      value?.id && value.id === number?.locationId
    );
    formik.setFieldValue(
      'brandId',
      sameAsSavedLocation ? number?.brandId || '' : ''
    );
    formik.setFieldValue(
      'brandName',
      sameAsSavedLocation ? number?.brandName || '' : ''
    );
  };

  const onLocationInputChange = (e) => {
    const inputValue = e.target.value;
    setSearchValue(inputValue);
    setSelectedLocation(null);
    formik.setFieldValue('locationName', inputValue);
    formik.setFieldValue('locationId', '');
    if (inputValue.trim()) {
      debouncedSearch(inputValue.trim());
    } else {
      debouncedSearch.cancel();
      dispatch(CLEAR_ACTIVE_LOCATIONS());
    }
  };

  const submitLabel = () => {
    if (saving) {
      return isEdit ? 'Saving...' : 'Creating...';
    }
    return isEdit ? 'Save' : 'Create';
  };

  return (
    <Dialog open scroll='body' onClose={handleClose}>
      <DialogContainer className='DialogMd'>
        <DialogTitle className='marginBottom2l'>
          {isEdit
            ? `Edit BYOC VA Number ${number.twilioPhone || ''}`
            : 'New BYOC VA Number'}
        </DialogTitle>
        <DialogBody>
          <Subtitle14px className='mT19'>Location name</Subtitle14px>
          <FlexContainer
            justifyContent='space-between'
            className='align-center'
          >
            <FlexItem flex={1} className='relativePosition'>
              <AutocompleteField
                className='w100'
                value={selectedLocation}
                options={activeLocations}
                name='locationName'
                optionLabelName='name'
                loading={searchingLocations}
                onChange={(e, value) => onLocationSelected(value)}
                inputOnChange={onLocationInputChange}
                hideDropdownOnEmptyInput
                searchValue={searchValue}
              />
              <ErrorSignWrapper className='positionInitial mTop4'>
                {(formik.touched.locationId && formik.errors.locationId) ||
                otterBrandsFailed ? (
                  <ErrorSign>
                    {(formik.touched.locationId && formik.errors.locationId) ||
                      'Could not load brands for this location'}
                  </ErrorSign>
                ) : null}
              </ErrorSignWrapper>
            </FlexItem>
          </FlexContainer>

          {isMultiBrand && (
            <>
              <Subtitle14px className='mT19'>Brand Id</Subtitle14px>
              <FlexContainer
                justifyContent='space-between'
                className='align-center'
              >
                <FlexItem flex={1} className='relativePosition'>
                  <AutocompleteField
                    className='w100'
                    value={formik.values.brandId || null}
                    options={otterBrands.storeIds}
                    name='brandId'
                    onChange={(e, value) => {
                      formik.setFieldValue('brandId', value || '');
                    }}
                    onBlur={() => formik.setFieldTouched('brandId', true)}
                  />
                  <ErrorSignWrapper className='positionInitial mTop4'>
                    {formik.touched.brandId && formik.errors.brandId ? (
                      <ErrorSign>{formik.errors.brandId}</ErrorSign>
                    ) : null}
                  </ErrorSignWrapper>
                </FlexItem>
              </FlexContainer>

              <Subtitle14px className='mT19'>Brand Name</Subtitle14px>
              <FlexContainer
                justifyContent='space-between'
                className='align-center'
              >
                <FlexItem flex={1} className='relativePosition'>
                  <Field
                    name='brandName'
                    value={formik.values.brandName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={
                      formik.touched.brandName && formik.errors.brandName
                        ? 'error'
                        : ''
                    }
                  />
                  <ErrorSignWrapper className='positionInitial mTop4'>
                    {formik.touched.brandName && formik.errors.brandName ? (
                      <ErrorSign>{formik.errors.brandName}</ErrorSign>
                    ) : null}
                  </ErrorSignWrapper>
                </FlexItem>
              </FlexContainer>
            </>
          )}

          <DialogActions className='justifyCenter'>
            <DialogAction className='w40'>
              <ButtonOutline
                type='button'
                onClick={handleClose}
                disabled={saving}
              >
                <span>Close</span>
              </ButtonOutline>
            </DialogAction>
            <DialogAction className='w40'>
              <ButtonPrimary
                type='button'
                onClick={formik.submitForm}
                disabled={saving || loadingOtterBrands || otterBrandsFailed}
              >
                <span>{submitLabel()}</span>
              </ButtonPrimary>
            </DialogAction>
          </DialogActions>
        </DialogBody>
      </DialogContainer>
    </Dialog>
  );
};

ByocVirtualNumberModal.propTypes = {
  number: PropTypes.shape({
    id: PropTypes.string,
    locationId: PropTypes.string,
    locationName: PropTypes.string,
    brandId: PropTypes.string,
    brandName: PropTypes.string,
    twilioPhone: PropTypes.string
  }),
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired
};

ByocVirtualNumberModal.defaultProps = {
  number: null
};

export default ByocVirtualNumberModal;
